import styles from "./affordability.module.css"
import React from "react"
import cx from "classnames"
import {Wizard} from "activities/components/wizard"
import * as str from "shared/string"
import * as util from "activities/util"
import * as fonts from "shared/fonts"
import * as shared from "activities/components/shared"
import * as inputs from "activities/components/inputs"
import {Input, Label} from "shared/components/inputs"
import * as callouts from "activities/components/callouts"

// Vacation Affordability Calculator
export const vacationExpenses = (props) => {
  const {resetKey, setState, step, colorClaims, expenses} = props
  const {expenseOptions, colors, stepStyles, calloutStyles, resultStyles, selectionStyles} = step.details
  const expenseColors = util.mapColors(colorClaims, colors, "expenses")
  // set the state value for vacationCost to util.sumCosts(expenses) on initial render

  return (
    <div className={stepStyles}>
      <div className={cx(styles["large-selections"], selectionStyles)}>
        <div className="selections large">
          <inputs.ExpenseForm
            className={styles["form"]}
            containerClassName={styles["expense-container"]}
            entryClassName={styles["dropdown"]}
            title="Travel Expenses"
            initialValue={expenses}
            integerLimit={10}
            onRowAdd={util.addRow(setState, "expenses")}
            onRowRemove={util.removeRow(setState, "expenses")}
            onRowChange={util.updateRow(setState, "expenses")}
            resetKey={resetKey}
            options={expenseOptions}
            colors={expenseColors}
            min={3}
            max={expenseOptions.length}
          />
        </div>
      </div>
      <callouts.Callout calloutStyles={calloutStyles}>
        <callouts.Detail label="Vacation Cost" result={util.dollars(util.sumCosts(expenses))} resultStyles={resultStyles} />
      </callouts.Callout>
    </div>
  )
}

export const vacationSavings = ({currentSavings, monthlyContribution, monthsToSave, updateState, step}) => {
  const {stepStyles, inputStyles} = step
  return (
    <div className={stepStyles}>
      <Label>How much is in your existing travel savings?</Label>
      <Input value={currentSavings} onChange={updateState("currentSavings", util.fromString)} placeholder={"$3,000"} style={inputStyles} />
      <Label>How much more can you contribute monthly?</Label>
      <Input value={monthlyContribution} onChange={updateState("monthlyContribution", util.fromString)} placeholder={"0"} style={inputStyles} />
      <Label>How many months do you have left to save?</Label>
      <Input value={monthsToSave} valueType="number" onChange={updateState("monthsToSave", util.fromString)} placeholder={"0"} style={inputStyles} />
    </div>
  )
}

export const vacationResults = ({expenses, currentSavings, monthlyContribution, monthsToSave, step}) => {
  const {results, questionStyles, answerStyles, resultStyles, statStyles, calloutStyles} = step.details
  const totalSavings = currentSavings + monthlyContribution * monthsToSave
  const vacationCost = util.sumCosts(expenses)
  const difference = totalSavings - vacationCost
  const result = difference > 0 ? results[0] : results[1]

  return (
    <div className={styles["results"]}>
      <div className={cx(styles["question"], questionStyles, fonts.getFontClass())}>Q: Can you afford this vacation?</div>
      <div className={cx(styles["answer"], answerStyles, fonts.getFontClass())}>A: {result.answer(util.dollars(Math.abs(difference)))}</div>
      <callouts.Callout className="inline" calloutStyles={calloutStyles}>
        <callouts.Detail label="Total Savings" result={util.dollars(totalSavings)} resultStyles={resultStyles} statStyles={statStyles} />
        <callouts.Detail label="Cost of Vacation" result={util.dollars(vacationCost)} resultStyles={resultStyles} statStyles={statStyles} />
      </callouts.Callout>
    </div>
  )
}

// Mortgage affordability calc
const MonthlyPaymentQuestion = ({resetKey, updateState, projectedMonthlyPayment, step}) => {
  const question = step.details.question
  return (
    <div className={styles["question-step"]}>
      <div id={`${str.toKebab(question)}`} className={cx(styles["lg-question"], fonts.getFontClass())}>
        {step.details.question}
      </div>
      <inputs.LongCalcInput
        aria-label={`${str.toKebab(question)}`}
        resetKey={resetKey}
        onChange={updateState("projectedMonthlyPayment", util.fromString)}
        integerLimit={5}
        initialValue={projectedMonthlyPayment}
      />
    </div>
  )
}

const onChangeMonthlyPaymentQuestion = (setState, props, name) => {
  const expenseIdx = props.monthlyExpenses.map((e) => e.name).indexOf(name)
  props.monthlyExpenses[expenseIdx] = {
    cost: props.projectedMonthlyPayment,
    name: name,
    colorId: expenseIdx
  }
  setState({monthlyExpenses: props.monthlyExpenses})
}

const MonthlyIncomeQuestion = ({resetKey, updateState, monthlyIncome, step}) => {
  const question = step.details.question
  return (
    <div className={styles["question-step"]}>
      <div id={`${str.toKebab(question)}`} className={cx(styles["lg-question"], fonts.getFontClass())}>
        {question}
      </div>
      <inputs.LongCalcInput
        ariaLabelledby={`${str.toKebab(question)}`}
        resetKey={resetKey}
        onChange={updateState("monthlyIncome", util.fromString)}
        integerLimit={5}
        initialValue={monthlyIncome}
      />
    </div>
  )
}

const getTotalExpenses = (monthlyExpenses, yearlyExpenses) => {
  const monthly = monthlyExpenses.map((expense) => expense.cost).filter((x) => x !== undefined)
  const yearly = yearlyExpenses.map((expense) => expense.cost).filter((x) => x !== undefined)
  const totalMonthlyExpenses = monthly.reduce((acc, val) => acc + val)
  const totalYearlyExpenses = yearly.reduce((acc, val) => acc + val)
  return totalMonthlyExpenses + totalYearlyExpenses / 12
}

const getNetIncome = (monthlyExpenses, yearlyExpenses, monthlyIncome) => monthlyIncome - getTotalExpenses(monthlyExpenses, yearlyExpenses)

const MonthlyExpensesQuestion = (props) => {
  const {resetKey, updateState, monthlyIncome, monthlyExpenses, yearlyExpenses, theme, setState, colorClaims, step} = props
  const {monthlyExpenseOptions, colors, yearlyExpenseOptions, monthlyIntegerLimit, yearlyIntegerLimit, lockedMonthlyOptions} = step.details
  const monthlyColors = util.mapColors(colorClaims, colors, "monthlyExpenses")
  const yearlyColors = util.mapColors(colorClaims, colors, "yearlyExpenses")
  return (
    <div className={styles["budget-step"]}>
      <div className={styles["large-selections"]}>
        <div className="selections large">
          <inputs.ExpenseForm
            className={styles["form"]}
            containerClassName={styles["expense-container"]}
            entryClassName={styles["dropdown"]}
            title="Monthly Expenses"
            initialValue={monthlyExpenses}
            integerLimit={monthlyIntegerLimit}
            onRowAdd={util.addRow(setState, "monthlyExpenses")}
            onRowRemove={util.removeRow(setState, "monthlyExpenses")}
            onRowChange={util.updateRow(setState, "monthlyExpenses")}
            resetKey={resetKey}
            options={monthlyExpenseOptions}
            colors={monthlyColors}
            theme={theme}
            min={3}
            max={monthlyExpenseOptions.length}
            lockedOptions={lockedMonthlyOptions}
          />
          <inputs.ExpenseForm
            className={styles["form"]}
            entryClassName={styles["dropdown"]}
            containerClassName={styles["expense-container"]}
            title="Yearly Expenses"
            initialValue={yearlyExpenses}
            integerLimit={yearlyIntegerLimit}
            onRowAdd={util.addRow(setState, "yearlyExpenses")}
            onRowRemove={util.removeRow(setState, "yearlyExpenses")}
            onRowChange={util.updateRow(setState, "yearlyExpenses")}
            resetKey={resetKey}
            options={yearlyExpenseOptions}
            colors={yearlyColors}
            theme={theme}
            min={2}
            max={yearlyExpenseOptions.length}
          />
        </div>
      </div>
      <callouts.EquationCallout>
        <callouts.EquationDetail label="Montlhy Income" result={util.formatDollarsNoCents(monthlyIncome)} data-testid="monthly-income" />
        <callouts.EquationDetail
          label="Total Expenses"
          result={util.formatDollarsNoCents(getTotalExpenses(monthlyExpenses, yearlyExpenses))}
          operand="subtraction"
          data-testid="total-expenses"
        />
        <callouts.EquationLine />
        <callouts.EquationDetail label="Net Income" result={util.formatDollarsNoCents(getNetIncome(monthlyExpenses, yearlyExpenses, monthlyIncome))} data-testid="net-income" />
      </callouts.EquationCallout>
    </div>
  )
}

const onChangeExpensesQuestion = (setState, props, name) => {
  const expenseIdx = props.monthlyExpenses.map((e) => e.name).indexOf(name)
  setState({projectedMonthlyPayment: props.monthlyExpenses[expenseIdx].cost})
}

const getNetIncomePercent = (monthlyIncome, netIncome) => netIncome / monthlyIncome

const getPaymentPercent = (monthlyIncome, projectedMonthlyPayment) => projectedMonthlyPayment / monthlyIncome

const getResults = (monthlyExpenses, yearlyExpenses, monthlyIncome, projectedMonthlyPayment, results) => {
  const netIncome = getNetIncome(monthlyExpenses, yearlyExpenses, monthlyIncome)
  const incomePercent = getNetIncomePercent(monthlyIncome, netIncome) * 100
  const mortgagePercent = getPaymentPercent(monthlyIncome, projectedMonthlyPayment) * 100
  switch (true) {
    case netIncome < 0:
      return results[6]
    case incomePercent >= 12 && mortgagePercent <= 25:
      return results[0]
    case incomePercent >= 8 && incomePercent < 12 && mortgagePercent <= 25:
      return results[1]
    case incomePercent >= 4 && incomePercent < 8 && mortgagePercent <= 25:
      return results[2]
    case mortgagePercent > 25 && mortgagePercent < 50:
      return results[3]
    case incomePercent < 4 && incomePercent > 0 && mortgagePercent < 50:
      return results[4]
    case mortgagePercent >= 50:
      return results[5]
    default:
      return results[6]
  }
}

const getMarkerColor = (percent) => {
  switch (true) {
    case percent <= 20:
      return "#FF4444"
    case percent > 20 && percent <= 50:
      return "#FFAB31"
    case percent > 50 && percent <= 55:
      return "#FFBB00"
    case percent > 55 && percent <= 75:
      return "#A7DD00"
    case percent > 75:
      return "#64D354"
    default:
      return "#FFBB00"
  }
}

const Results = ({projectedMonthlyPayment, monthlyIncome, monthlyExpenses, yearlyExpenses, step}) => {
  const result = getResults(monthlyExpenses, yearlyExpenses, monthlyIncome, projectedMonthlyPayment, step.details.results)
  const netIncome = getNetIncome(monthlyExpenses, yearlyExpenses, monthlyIncome)
  const incomePercent = getNetIncomePercent(monthlyIncome, netIncome)
  return (
    <div className={styles["results"]}>
      <shared.ColoredRangeGraph percent={result.value} color={getMarkerColor(result.value)} />
      <div className={cx(styles["answer"], fonts.getFontClass())}>{result.answer}</div>
      <div className={cx(styles["message"], fonts.getFontClass())}>{result.message(util.formatPercent(incomePercent), util.formatDollarsNoCents(netIncome))}</div>
      <callouts.Callout calloutStyles={styles["stats"]}>
        <callouts.Detail statStyles={styles["stat"]} label={step.details.goalLabel} result={util.formatDollarsNoCents(projectedMonthlyPayment)} />
        <callouts.Detail statStyles={styles["stat"]} label="Monthly Income" result={util.formatDollarsNoCents(monthlyIncome)} />
        <callouts.Detail statStyles={styles["stat"]} label="Net Income" result={util.formatDollarsNoCents(getNetIncome(monthlyExpenses, yearlyExpenses, monthlyIncome))} />
      </callouts.Callout>
    </div>
  )
}

export default (props) => <AffordabilityCalculator_ {...props} />

const AffordabilityCalculator_ = shared.ActivityFactory(
  ({getProps, getState, updateState, setState, onReset}) => {
    const {resetKey, projectedMonthlyPayment, monthlyIncome, monthlyExpenses, yearlyExpenses, colorClaims} = getState()
    const {steps, theme} = getProps()
    return (
      <div className={styles["wizard"]}>
        <Wizard
          resetKey={resetKey}
          onReset={onReset}
          setup={{
            steps: steps,
            theme: theme,
            props: {
              projectedMonthlyPayment: projectedMonthlyPayment,
              monthlyIncome: monthlyIncome,
              monthlyExpenses: monthlyExpenses,
              yearlyExpenses: yearlyExpenses,
              colorClaims: colorClaims,
              setState: setState,
              updateState: updateState
            }
          }}
        />
      </div>
    )
  },
  {
    defaultProps: {
      className: styles["affordability-calculator"],
      steps: [
        {
          title: "Can You Afford This?",
          subtitle: "Give us a little information and we'll tell you how realistic it'd be to pay for your stuff over time.",
          isStepComplete: (stepProps) => (stepProps.projectedMonthlyPayment > 0 ? true : false),
          onChangeStep: (setState, stepProps) => onChangeMonthlyPaymentQuestion(setState, stepProps, "Mortgage"),
          content: MonthlyPaymentQuestion,
          details: {
            question: "What do you plan to spend monthly?"
          },
          buttonText: "Next"
        },
        {
          title: "Can You Afford This?",
          subtitle: "Okay, now enter your monthly income.",
          isStepComplete: (stepProps) => (stepProps.monthlyIncome > 0 ? true : false),
          content: MonthlyIncomeQuestion,
          details: {
            question: "What is your monthly income?"
          },
          buttonText: "Next"
        },
        {
          title: "Can You Afford This?",
          subtitle: "Okay, now enter your monthly income and divide it into a few expense categories.",
          isStepComplete: (stepProps) => (stepProps.monthlyIncome > 0 ? true : false),
          onChangeStep: (setState, stepProps) => onChangeExpensesQuestion(setState, stepProps, "Mortgage"),
          content: MonthlyExpensesQuestion,
          details: {
            colors: [
              "#FFBB00",
              "#64D254",
              "#C4A8DF",
              "#FF91B0",
              "#FF4444",
              "#6EC5EF",
              "#005F8B",
              "#00BDDC",
              "#65DFC5",
              "#9E6CC7",
              "#FF881F",
              "#FFE7AF",
              "#00637A",
              "#FFCBCF",
              "#006AF9",
              "#FFA971",
              "#EDFCAE",
              "#A7DD00",
              "#077259",
              "#A8AFDA"
            ],
            monthlyExpenseOptions: ["Utilities", "Phone", "Internet", "Car Payment", "Car Insurance", "Car Repairs", "Gas", "Food", "Entertainment", "Clothing", "Debt"],
            yearlyExpenseOptions: ["Life Insurance", "Medical", "Vacation", "Furniture", "Gifts", "Emergency", "Other"],
            monthlyIntegerLimit: 5,
            yearlyIntegerLimit: 6
          },
          buttonText: "Finish"
        },
        {
          title: "Can You Afford This?",
          subtitle: "Let's take a look at the results.",
          isStepComplete: () => true,
          content: Results,
          details: {
            question: "Can you afford this?",
            goalLabel: "Monthly Cost",
            results: [
              {
                answer: "Yes, you can afford this. No problems.",
                message: (savings) =>
                  "You're well within the ability to afford this mortgage. You're saving " +
                  savings +
                  " of your monthly income, after the mortgage; that's good. Don't get cocky, though: if you have other debts, or if you didn't account for your savings goals in the budget, consider giving it another go to make sure you're thinking of everything.",
                value: 90
              },
              {
                answer: "Yes, you can afford this. You're safe",
                message: (savings) =>
                  "As long as you've included bills, expenses, savings goals, and debt pay down in your budget, you're probably safe. You're saving " +
                  savings +
                  " of your monthly income, after the mortgage; that's good. Keep it up. Resist the urge to buy more house than you need, and save as much  of that extra income as you can.",
                value: 70
              },
              {
                answer: "Mmm, you can barely afford this.",
                message: (savings, remaining) =>
                  "You can probably pull it off technically, however, proceed with caution. You only have " +
                  remaining +
                  " to spare, after expenses. And if you've left anything off your budget—like savings goals, or debt pay down—you're house poor.",
                value: 65
              },
              {
                answer: "Mmm, you can barely afford this.",
                message: () =>
                  "Well... you seem to have spare cash, but you're violating a common house-buying rule of thumb: keep your mortgage below 25% of your monthly income. This is important because you need to be able to weather financial storms, like unexpected expenses or a sudden shock to your income. Consider waiting, or find a cheaper home.",
                value: 65
              },
              {
                answer: "Can you afford this? Probably not, no.",
                message: (savings) =>
                  "You should either wait to buy, or find a cheaper home: you're too close to the edge. You're saving " +
                  savings +
                  " of your monthly income, which is not enough slack to weather common financial storms like unexpected expenses or even mild changes in income.",
                value: 25
              },
              {
                answer: "Can you afford this? Probably not, no.",
                message: () =>
                  "Technically you have enough loose cash, but your mortgage represents way too much of your income. Try to keep your mortgage payment to less than 25% of your monthly pay.",
                value: 25
              },
              {
                answer: "Can you afford this? Definitely not.",
                message: () =>
                  "Your net income is negative. That means you will spend more than you earn for this mortgage. In no circumstances should you commit to a house you can't actually make payments on. Even if a bank offers you a loan, if you can't clear your monthly expenses (with room to spare), breathe, take a step back, and think of some different options.",
                value: 0
              }
            ]
          },
          buttonText: "Start Over"
        }
      ]
    },
    stateFilter: (state) => state,
    defaultState: {
      colorClaims: [],
      projectedMonthlyPayment: 0,
      monthlyIncome: 0,
      monthlyExpenses: [
        {name: "Mortgage", cost: 0},
        {name: "Food", cost: 250},
        {name: "Entertainment", cost: 50},
        {name: "Car Payment", cost: 230}
      ],
      yearlyExpenses: [
        {name: "Medical", cost: 3000},
        {name: "Gifts", cost: 200},
        {name: "Emergency", cost: 1000}
      ]
    }
  }
)
